import { Injectable } from '@angular/core';
import { TransactionStore } from './transaction.store';
import { Transaction, TransactionType } from './transaction.model';
import { formatDate } from '@angular/common';
import { Observable, of } from 'rxjs';
import { tap, map, mapTo } from 'rxjs/operators';
import { TransactionQuery } from './transaction.query';
import { config } from './../../shared/config';
import { ApiService } from '../../shared/services/core/api.service';
import moment from 'moment/moment';

@Injectable({ providedIn: 'root' })
export class TransactionService {
  constructor(private transactionStore: TransactionStore, private api: ApiService, private transactionQuery: TransactionQuery) {}

  // check if the submitted date range is contained within a previously submitted range
  isCached(startDate: Date, endDate: Date, type: TransactionType): boolean {
    return (
      this.transactionQuery.getValue().cachedDates[type].filter((q) => q.startDate <= startDate && q.endDate >= endDate).length >
      0
    );
  }

  fetchTransactions(startDate: Date, endDate: Date, memberId: string, type: TransactionType) {
    if (this.isCached(startDate, endDate, type)) {
      return of(null);
    }

    let source: Observable<Transaction[]> = of([]);
    const fromDate = encodeURI(formatDate(startDate, 'yyyy-MM-dd', 'en-GB'));
    const toDate = formatDate(endDate, 'yyyy-MM-dd', 'en-GB');

    switch (type) {
      case TransactionType.ecash:
        source = this.api.get<Transaction>(
          'purse/transactions/' + config.purseConfig.combined + '/' + fromDate + '/' + toDate + '/' + memberId
        );
        break;
      case TransactionType.loyalty:
        source = this.api.get<Transaction>(
          'purse/transactions/' + config.purseConfig.virtualLoyalty + '/' + fromDate + '/' + toDate + '/' + memberId
        );
        break;
      case TransactionType.payment:
        source = this.api.get<Transaction>(
          'purse/paymenttransactions/' + config.purseConfig.combined + '/' + fromDate + '/' + toDate
        );

        break;
      case TransactionType.cardlink:
        startDate.setHours(0, 0, 0);
        endDate.setHours(23, 59, 59);
        const cardLinkFromDate = encodeURI(formatDate(startDate, 'yyyy-MM-dd HH:mm', 'en-GB'));
        const cardLinkToDate = encodeURI(formatDate(endDate, 'yyyy-MM-dd HH:mm', 'en-GB'));
        source = this.api.get<Transaction>('affiliates/transactions/' + cardLinkFromDate + '/' + cardLinkToDate + '/' + memberId);
        break;
    }

    return source.pipe(
      map((x) => {
        x.forEach((t) => {
          (t.TransactionDate = moment.utc(t.TransactionDate).local().toDate()), (t.Type = type);
        });
        return x;
      }),
      tap((x) => {
        this.transactionStore.upsertMany(x);
      }),
      tap(() => this.transactionStore.addCachedDate(startDate, endDate, type)),
      mapTo(null)
    );
  }
}
